/**
 * Members List Page Component - FULLY FIXED VERSION
 * Hope SACCO - Central page for viewing and managing all members
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import globalApi from '../utils/globalFetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MembersListPage = () => {
  const navigate = useNavigate();
  
  const [members, setMembers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  
  const [filters, setFilters] = useState({
    status: '',
    joinYear: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalMembers: 0,
    limit: 25,
    hasNext: false,
    hasPrev: false,
    showing: { from: 0, to: 0 }
  });

  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Left Members tab
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'left'
  const [leftMembers, setLeftMembers] = useState([]);
  const [leftLoading, setLeftLoading] = useState(false);
  const [leftError, setLeftError] = useState('');
  const [expandedLeftMember, setExpandedLeftMember] = useState(null); // memberId of expanded row

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await globalApi.get('/members/statistics');
      if (response.status === 429) {
        setError('Rate limited - please wait a moment before refreshing');
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch statistics');
      const data = await response.json();
      setStatistics(data.data);
    } catch (error) {
      if (error.message === 'Session expired') {
        return;
      }
      console.error('Statistics error:', error);
    }
  }, []);

  const fetchLeftMembers = useCallback(async () => {
    if (activeTab !== 'left') return;
    setLeftLoading(true);
    setLeftError('');
    try {
      const response = await globalApi.get('/members/left');
      if (response.status === 429) {
        setLeftError('Rate limited - please wait a moment before refreshing');
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch left members');
      const data = await response.json();
      setLeftMembers(data.data?.members || []);
    } catch (error) {
      if (error.message === 'Session expired') {
        return;
      }
      setLeftError(error.message || 'Failed to load left members');
    } finally {
      setLeftLoading(false);
    }
  }, [activeTab]);

  const fetchMembers = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(searchDebounce && { search: searchDebounce }),
        ...(filters.status && { status: filters.status }),
        ...(filters.joinYear && { joinYear: filters.joinYear }),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      const response = await globalApi.get(`/members?${params}`);

      if (response.status === 429) {
        setError('Rate limited - please wait a moment before refreshing');
        setIsLoading(false);
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch members');

      const data = await response.json();
      
      setMembers(data.data.members || []);
      
      const total = data.data.total || 0;
      const limit = data.data.limit || 25;
      const currentPage = data.data.currentPage || 1;
      const totalPages = Math.ceil(total / limit);
      
      setPagination({
        currentPage,
        totalPages,
        totalMembers: total,
        limit,
        hasNext: currentPage < totalPages,
        hasPrev: currentPage > 1,
        showing: {
          from: total === 0 ? 0 : ((currentPage - 1) * limit) + 1,
          to: Math.min(currentPage * limit, total)
        }
      });

    } catch (error) {
      if (error.message === 'Session expired') {
        return;
      }
      setError(error.message || 'Failed to load members');
      console.error('Fetch members error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, pagination.currentPage, pagination.limit, searchDebounce, filters]);

  useEffect(() => {
    fetchMembers();
    fetchStatistics();
  }, [fetchMembers, fetchStatistics]);

  useEffect(() => {
    fetchLeftMembers();
  }, [fetchLeftMembers]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ 
      ...prev, 
      limit: parseInt(newLimit),
      currentPage: 1
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: '',
      joinYear: '',
      sortBy: 'name',
      sortOrder: 'asc'
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `Ksh ${num.toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '—';
    try {
      let date;
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        date = new Date(dateValue);
      }
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return '—';
    }
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'status-badge';
    switch(status?.toLowerCase()) {
      case 'active': return `${baseClass} status-active`;
      case 'inactive': return `${baseClass} status-inactive`;
      case 'suspended': return `${baseClass} status-suspended`;
      default: return baseClass;
    }
  };

  const getJoinYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 10; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const params = new URLSearchParams({
        ...(searchDebounce && { search: searchDebounce }),
        ...(filters.status && { status: filters.status }),
        ...(filters.joinYear && { joinYear: filters.joinYear }),
        format: 'excel'
      });

      const response = await globalApi.get(`/members/export?${params}`);

      if (response.status === 429) {
        alert('Rate limited - please wait before exporting again');
        return;
      }

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const result = await response.json();
      const rows = result.data;

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Members');
      XLSX.writeFile(wb, `members-export-${new Date().toISOString().split('T')[0]}.xlsx`);

    } catch (error) {
      if (error.message === 'Session expired') {
        return;
      }
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleToggleStatus = async (memberId, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    
    if (!window.confirm(`Are you sure you want to ${newStatus === 'Active' ? 'activate' : 'deactivate'} this member?`)) {
      return;
    }

    try {
      const response = await globalApi.put(`/members/${memberId}/status`, { status: newStatus });

      if (response.status === 429) {
        alert('Rate limited - please wait before trying again');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Status update failed');
      }

      setActiveMenu(null);

      setMembers(prevMembers => 
        prevMembers.map(member => 
          member.memberId === memberId 
            ? { ...member, saccoInfo: { ...member.saccoInfo, status: newStatus } }
            : member
        )
      );

      fetchStatistics();
      
      alert(`Member ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully`);

    } catch (error) {
      if (error.message === 'Session expired') {
        return;
      }
      console.error('Status update error:', error);
      alert(`Failed to update member status: ${error.message}`);
      setActiveMenu(null);
    }
  };

  const toggleActionMenu = (memberId, event) => {
    if (activeMenu === memberId) {
      setActiveMenu(null);
      return;
    }

    const button = event.currentTarget;
    const buttonRect = button.getBoundingClientRect();
    const menuWidth = 180;
    const menuHeight = 200;
    
    // Calculate best position
    let top = buttonRect.bottom + 5;
    let left = buttonRect.right - menuWidth;
    
    // Check if menu goes below viewport
    if (top + menuHeight > window.innerHeight) {
      top = buttonRect.top - menuHeight - 5;
    }
    
    // Check if menu goes off left edge
    if (left < 10) {
      left = 10;
    }
    
    // Check if menu goes off right edge
    if (left + menuWidth > window.innerWidth - 10) {
      left = window.innerWidth - menuWidth - 10;
    }
    
    setMenuPosition({ top, left });
    setActiveMenu(memberId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeMenu && !event.target.closest('.actions-cell')) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenu]);

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxButtons - 1);
    
    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <button key={1} onClick={() => handlePageChange(1)} className="pagination-btn">
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis-start" className="pagination-ellipsis">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${i === pagination.currentPage ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) {
        buttons.push(<span key="ellipsis-end" className="pagination-ellipsis">...</span>);
      }
      buttons.push(
        <button
          key={pagination.totalPages}
          onClick={() => handlePageChange(pagination.totalPages)}
          className="pagination-btn"
        >
          {pagination.totalPages}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="members-list-page" style={{ padding: '20px', maxWidth: '100%' }}>
      <div className="page-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>Members</h1>
          <div className="breadcrumb" style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
            <span onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer', color: '#4b3ff6' }}>
              Dashboard
            </span>
            <span style={{ margin: '0 8px' }}>›</span>
            <span>Members</span>
          </div>
        </div>
        <button onClick={() => navigate('/admin/members/add')} style={{ padding: '10px 20px', backgroundColor: '#4b3ff6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>
          + Add New Member
        </button>
      </div>

      {statistics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ width: '50px', height: '50px', backgroundColor: '#4b3ff6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: 'white' }}>
              👥
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '600', marginBottom: '5px' }}>TOTAL MEMBERS</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{statistics.totalMembers || 0}</div>
            </div>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ width: '50px', height: '50px', backgroundColor: '#10b981', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: 'white' }}>
              ✓
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '600', marginBottom: '5px' }}>ACTIVE MEMBERS</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{statistics.activeMembers || 0}</div>
            </div>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ width: '50px', height: '50px', backgroundColor: '#f59e0b', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: 'white' }}>
              💰
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '600', marginBottom: '5px' }}>TOTAL SHARE CAPITAL</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>{formatCurrency(statistics.totalShareCapital)}</div>
            </div>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ width: '50px', height: '50px', backgroundColor: '#8b5cf6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: 'white' }}>
              📊
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '600', marginBottom: '5px' }}>NEW THIS MONTH</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>{statistics.newThisMonth || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #e5e7eb' }}>
        {[{ key: 'active', label: '👥 Active Members' }, { key: 'left', label: '🗂️ Left Members' }].map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            padding: '10px 22px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
            color: activeTab === key ? '#4b3ff6' : '#6b7280',
            borderBottom: activeTab === key ? '2px solid #4b3ff6' : '2px solid transparent',
            marginBottom: -2,
          }}>{label}</button>
        ))}
      </div>

      {activeTab === 'active' && (
      <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 300px' }}>
            <input
              type="text"
              placeholder="Search by name, ID, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '10px 40px 10px 10px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#999' }}>
                ×
              </button>
            )}
          </div>

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }}
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

          <select
            value={filters.joinYear}
            onChange={(e) => handleFilterChange('joinYear', e.target.value)}
            style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }}
          >
            <option value="">All Years</option>
            {getJoinYears().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <select
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }}
          >
            <option value="name">Name (A-Z)</option>
            <option value="memberId">Member ID</option>
            <option value="joinDate">Join Date</option>
            <option value="shareBalance">Share Balance</option>
          </select>

          {(filters.status || filters.joinYear || searchTerm) && (
            <button onClick={clearFilters} style={{ padding: '10px 15px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
              Clear Filters
            </button>
          )}

          <button 
            onClick={handleExport} 
            disabled={isExporting || members.length === 0}
            style={{ padding: '10px 15px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', opacity: (isExporting || members.length === 0) ? 0.5 : 1 }}
          >
            Export to Excel
          </button>
        </div>

        {error && (
          <div style={{ padding: '15px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '5px', marginBottom: '15px' }}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div>Loading members...</div>
          </div>
        ) : members.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>No members found</p>
            {(searchTerm || filters.status || filters.joinYear) && (
              <button onClick={clearFilters} style={{ marginTop: '10px', padding: '8px 15px', backgroundColor: '#4b3ff6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                Clear filters to see all members
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#4b3ff6', color: 'white' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>MEMBER ID</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>NAME</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>PHONE NUMBER</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>EMAIL</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>JOIN DATE</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>SHARE BALANCE</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>TOTAL LOANS</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600' }}>STATUS</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member, idx) => (
                    <tr key={member.id || member.memberId} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white' }}>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{ fontWeight: '600', color: '#4b3ff6' }}>{member.memberId}</span>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <strong>{member.personalInfo?.fullName || 'N/A'}</strong>
                      </td>
                      <td style={{ padding: '12px 8px' }}>{member.personalInfo?.phoneNumber || 'N/A'}</td>
                      <td style={{ padding: '12px 8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {member.personalInfo?.email || 'N/A'}
                      </td>
                      <td style={{ padding: '12px 8px' }}>{formatDate(member.saccoInfo?.dateJoined)}</td>
                      <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                        {formatCurrency(member.currentBalances?.shares)}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                        {formatCurrency(member.totalLoans)}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <span style={{ 
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: member.saccoInfo?.status === 'Active' ? '#d1fae5' : '#fee2e2',
                          color: member.saccoInfo?.status === 'Active' ? '#065f46' : '#991b1b'
                        }}>
                          {member.saccoInfo?.status || 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <div className="actions-cell" style={{ position: 'relative' }}>
                          <button
                            onClick={(e) => toggleActionMenu(member.memberId, e)}
                            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '5px 10px' }}
                          >
                            ...
                          </button>
                          {activeMenu === member.memberId && (
                            <div 
                              style={{
                                position: 'fixed',
                                top: menuPosition.top + 'px',
                                left: menuPosition.left + 'px',
                                zIndex: 9999,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                minWidth: '180px',
                                overflow: 'hidden'
                              }}
                            >
                              <button onClick={() => {
                                setActiveMenu(null);
                                navigate(`/admin/members/${member.memberId}`);
                              }} style={{ width: '100%', padding: '12px 16px', border: 'none', backgroundColor: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '14px', display: 'block' }}>
                                View Profile
                              </button>
                              <button onClick={() => {
                                setActiveMenu(null);
                                navigate(`/admin/reports/member-statement?memberId=${member.memberId}`);
                              }} style={{ width: '100%', padding: '12px 16px', border: 'none', backgroundColor: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '14px', borderTop: '1px solid #f0f0f0', display: 'block' }}>
                                View Statement
                              </button>
                              <button onClick={() => {
                                setActiveMenu(null);
                                navigate(`/admin/members/${member.memberId}/edit`);
                              }} style={{ width: '100%', padding: '12px 16px', border: 'none', backgroundColor: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '14px', borderTop: '1px solid #f0f0f0', display: 'block' }}>
                                Edit Member
                              </button>
                              <button onClick={() => {
                                setActiveMenu(null);
                                navigate(`/admin/members/${member.memberId}/exit`);
                              }} style={{ width: '100%', padding: '12px 16px', border: 'none', backgroundColor: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '14px', borderTop: '1px solid #f0f0f0', color: '#7c3aed', fontWeight: '600', display: 'block' }}>
                                🚪 Exit Member
                              </button>

                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Showing {pagination.showing.from} to {pagination.showing.to} of {pagination.totalMembers} members
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select 
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(e.target.value)}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '14px' }}
                >
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>

                <div style={{ display: 'flex', gap: '5px' }}>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: 'white', cursor: pagination.hasPrev ? 'pointer' : 'not-allowed', opacity: pagination.hasPrev ? 1 : 0.5 }}
                  >
                    Previous
                  </button>
                  
                  {renderPaginationButtons()}
                  
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: 'white', cursor: pagination.hasNext ? 'pointer' : 'not-allowed', opacity: pagination.hasNext ? 1 : 0.5 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      )}

      {/* ── LEFT MEMBERS TAB ── */}
      {activeTab === 'left' && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginTop: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#374151' }}>
            🗂️ Archived — Left Members ({leftMembers.length})
          </h3>
          {leftError && (
            <div style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '5px', marginBottom: '15px' }}>
              {leftError}
            </div>
          )}
          {leftLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading…</div>
          ) : leftMembers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              No members have left the SACCO yet.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#6b7280', color: 'white' }}>
                    <th style={{ padding: '10px 8px', textAlign: 'left' }}>MEMBER ID</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left' }}>NAME</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left' }}>LEFT DATE</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>SHARES AT EXIT</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>REFUNDED</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left' }}>SHARE CAPITAL</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left' }}>REASON</th>
                  </tr>
                </thead>
                <tbody>
                  {leftMembers.map((m, idx) => {
                    const isExpanded = expandedLeftMember === m.memberId;
                    const leftMonthStr = m.leftDate
                      ? `${new Date(m.leftDate).getFullYear()}-${String(new Date(m.leftDate).getMonth() + 1).padStart(2, '0')}`
                      : '';
                    return (
                      <React.Fragment key={m.memberId}>
                        {/* ── Main row — click to expand ── */}
                        <tr
                          onClick={() => setExpandedLeftMember(isExpanded ? null : m.memberId)}
                          style={{
                            borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0',
                            backgroundColor: isExpanded ? '#f0f0ff' : idx % 2 === 0 ? '#fafafa' : 'white',
                            cursor: 'pointer',
                          }}
                        >
                          <td style={{ padding: '10px 8px', fontWeight: 600, color: '#4b3ff6' }}>{m.memberId}</td>
                          <td style={{ padding: '10px 8px' }}>
                            <strong style={{ color: '#111' }}>{m.memberName}</strong>
                            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>
                              {isExpanded ? '▲ collapse' : '▼ details'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 8px' }}>
                            {m.leftDate ? new Date(m.leftDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right' }}>Ksh {(m.sharesAtExit || 0).toLocaleString()}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right' }}>Ksh {(m.sharesRefunded || 0).toLocaleString()}</td>
                          <td style={{ padding: '10px 8px', fontSize: 12 }}>
                            {m.shareCapitalTransferType === 'transfer'
                              ? <span style={{ color: '#10b981' }}>→ {m.shareCapitalBoughtByName || m.shareCapitalBoughtBy}</span>
                              : m.shareCapitalTransferType === 'none' || m.shareCapitalAmount === 0
                              ? <span style={{ color: '#6b7280' }}>None (no shares)</span>
                              : <span style={{ color: '#f59e0b' }}>Retained in SACCO</span>}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#6b7280', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.leftReason || '—'}
                          </td>
                        </tr>

                        {/* ── Expanded detail panel ── */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0, borderBottom: '2px solid #e0e0ff' }}>
                              <div style={{ background: '#f8f8ff', padding: '16px 20px', borderLeft: '4px solid #4b3ff6' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 32px', fontSize: 13, marginBottom: 16 }}>
                                  {[
                                    ['Member ID', m.memberId],
                                    ['Full Name', m.memberName],
                                    ['Email', m.email || '—'],
                                    ['Phone', m.phoneNumber || '—'],
                                    ['Left Date', m.leftDate ? new Date(m.leftDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'],
                                    ['Reason for Leaving', m.leftReason || '—'],
                                    ['Shares at Exit', `Ksh ${(m.sharesAtExit || 0).toLocaleString()}`],
                                    ['Amount Refunded', `Ksh ${(m.sharesRefunded || 0).toLocaleString()}`],
                                    ['Share Capital (Ksh 5,000)', m.shareCapitalTransferType === 'transfer'
                                      ? `Transferred to ${m.shareCapitalBoughtByName || m.shareCapitalBoughtBy} on ${m.shareCapitalTransferDate ? new Date(m.shareCapitalTransferDate).toLocaleDateString('en-GB') : '—'}`
                                      : m.shareCapitalAmount > 0 ? 'Retained in SACCO' : 'None (member had no shares)'],
                                    ['Processed By', m.exitProcessedBy || '—'],
                                  ].map(([label, value]) => (
                                    <div key={label}>
                                      <span style={{ color: '#6b7280', display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                                      <span style={{ fontWeight: 500 }}>{value}</span>
                                    </div>
                                  ))}
                                </div>
                                {m.exitNotes && (
                                  <div style={{ fontSize: 12, color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', marginBottom: 14 }}>
                                    <strong>Exit Notes:</strong> {m.exitNotes}
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: 10 }}>
                                  <button
                                    onClick={e => { e.stopPropagation(); navigate(`/admin/reports/merged-statement?memberId=${m.memberId}${leftMonthStr ? `&toMonth=${leftMonthStr}` : ''}`); }}
                                    style={{ padding: '7px 16px', background: '#4b3ff6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    📄 View Statement
                                  </button>
                                  <button
                                    onClick={e => { e.stopPropagation(); navigate('/admin/members/manage-shares', { state: { memberId: m.memberId, memberName: m.memberName } }); }}
                                    style={{ padding: '7px 16px', background: '#fff', color: '#4b3ff6', border: '1px solid #4b3ff6', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    📊 View Shares History
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MembersListPage;
